import ObjectSelect, { type ObjectSelectValue } from "@components/ObjectSelect";
import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import {
  type DirectoryGroup,
  type DirectoryGroupChannel,
  type DirectoryGroupMember,
  useAdminGroup,
  useAdminGroups,
  useCreateAdminGroup,
  useDeleteAdminGroup,
  useUpdateAdminGroup,
} from "@features/groups/api";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import EnterpriseTable from "@shared/ui/enterprise-table";
import EntityHeaderActions from "@shared/ui/entity-header-actions";
import PageLayout from "@shared/ui/page-layout";
import { useStandardPagination } from "@shared/useStandardPagination";
import {
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

interface GroupFormValues {
  name?: string;
  level?: number;
  measurement_type?: number;
  interval_minutes?: number;
  balance_element?: number;
  zone?: number;
  note?: string;
}

const AdminGroupDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<GroupFormValues>();
  const groupQuery = useAdminGroup(id);
  const createGroup = useCreateAdminGroup();
  const updateGroup = useUpdateAdminGroup();
  const deleteGroup = useDeleteAdminGroup();
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];

  const [selectedObject, setSelectedObject] = useState<ObjectSelectValue>();
  const [channels, setChannels] = useState<DirectoryGroupChannel[]>([]);
  const [members, setMembers] = useState<DirectoryGroupMember[]>([]);
  const [channelSearch, setChannelSearch] = useState("");

  const level = Form.useWatch("level", form) ?? 1;

  useEffect(() => {
    if (!groupQuery.data) {
      form.setFieldsValue({ level: 1 });
      return;
    }
    const group = groupQuery.data;
    form.setFieldsValue({
      name: group.name,
      level: group.level,
      measurement_type: group.measurement_type ?? undefined,
      interval_minutes: group.interval_minutes ?? undefined,
      balance_element: group.balance_element ?? undefined,
      zone: group.zone ?? undefined,
      note: group.note ?? undefined,
    });
    setSelectedObject({ regionId: group.region_id, objectId: group.object_id });
    setChannels(group.channels ?? []);
    setMembers(group.members ?? []);
  }, [form, groupQuery.data]);

  const channelsQuery = useChannelsFlat(selectedObject?.objectId, channelSearch || undefined, 500);
  const availableChannels = channelsQuery.data ?? [];
  const channelLookup = useMemo(() => {
    const map = new Map<number, ChannelMeterRow>();
    availableChannels.forEach((row) => {
      map.set(row.meter_number, row);
    });
    return map;
  }, [availableChannels]);

  const memberOptionsQuery = useAdminGroups(
    selectedObject
      ? {
          object: selectedObject,
          level: 1,
        }
      : undefined,
  );
  const availableGroups = useMemo(
    () => (memberOptionsQuery.data ?? []).filter((group) => group.id !== id),
    [id, memberOptionsQuery.data],
  );

  const channelColumns: ColumnsType<DirectoryGroupChannel> = [
    { title: "ID", dataIndex: "channel_id", width: 140 },
    {
      title: "Объект",
      dataIndex: "object_id",
      render: (_value, record) => `№${record.object_id} (регион ${record.region_id})`,
    },
    {
      title: "Подстанция",
      dataIndex: "feeder_id",
      render: (value?: number) => (value ? `Фидер ${value}` : "—"),
    },
    {
      title: "Действия",
      dataIndex: "actions",
      width: 120,
      render: (_value, record) => (
        <Button size="small" danger onClick={() => removeChannel(record.channel_id)}>
          Удалить
        </Button>
      ),
    },
  ];
  const channelPagination = useStandardPagination(channels.length);

  const memberColumns: ColumnsType<DirectoryGroupMember> = [
    { title: "ID", dataIndex: "child_group_id" },
    {
      title: "Действия",
      dataIndex: "actions",
      width: 120,
      render: (_value, record) => (
        <Button size="small" danger onClick={() => removeMember(record.child_group_id)}>
          Удалить
        </Button>
      ),
    },
  ];
  const memberPagination = useStandardPagination(members.length);

  const addChannel = (meterNumber: number) => {
    if (!selectedObject) {
      message.warning("Сначала выберите объект");
      return;
    }
    if (channels.some((channel) => channel.channel_id === meterNumber)) {
      return;
    }
    const details = channelLookup.get(meterNumber);
    if (!details) {
      return;
    }
    setChannels((prev) => [
      ...prev,
      {
        channel_id: details.meter_number,
        region_id: details.region_id,
        object_id: details.object_id,
        feeder_id: details.feeder_id,
      },
    ]);
  };

  const removeChannel = (channelId: number) => {
    setChannels((prev) => prev.filter((channel) => channel.channel_id !== channelId));
  };

  const addMember = (groupId: string) => {
    if (members.some((member) => member.child_group_id === groupId)) {
      return;
    }
    setMembers((prev) => [...prev, { child_group_id: groupId }]);
  };

  const removeMember = (groupId: string) => {
    setMembers((prev) => prev.filter((member) => member.child_group_id !== groupId));
  };

  const handleSave = async () => {
    if (!selectedObject) {
      message.error("Выберите объект");
      return;
    }
    try {
      const values = await form.validateFields();
      const payload: DirectoryGroup = {
        id: groupQuery.data?.id ?? "",
        region_id: selectedObject.regionId,
        object_id: selectedObject.objectId,
        level: values.level ?? 1,
        name: values.name ?? `Группа ${selectedObject.objectId}`,
        measurement_type: values.measurement_type,
        interval_minutes: values.interval_minutes,
        balance_element: values.balance_element,
        zone: values.zone,
        note: values.note,
        channels,
        members,
      };
      if (isNew) {
        await createGroup.mutateAsync({ data: payload });
        message.success("Группа создана");
      } else if (id) {
        await updateGroup.mutateAsync({ id, data: payload });
        message.success("Группа обновлена");
      }
      navigate("/admin/groups");
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) {
      return;
    }
    try {
      await deleteGroup.mutateAsync({ id });
      message.success("Группа удалена");
      navigate("/admin/groups");
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  return (
    <PageLayout
      title={isNew ? "Новая группа" : `Группа ${groupQuery.data?.name ?? id}`}
      copyUrl={id && id !== "new" ? `/admin/groups/${encodeURIComponent(id)}` : undefined}
      extra={
        <EntityHeaderActions
          onBack={() => navigate(-1)}
          onSave={handleSave}
          saveLoading={createGroup.isPending || updateGroup.isPending}
          onDelete={!isNew && id ? handleDelete : undefined}
          deleteLoading={deleteGroup.isPending}
          deleteConfirmTitle="Удалить группу?"
        />
      }
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Здесь можно изменить параметры группы, добавить или убрать счётчики и настроить
          вложенность.
        </Typography.Text>

        <Card>
          <Form layout="vertical" form={form} initialValues={{ level: 1 }} autoComplete="off">
            <Form.Item label="Объект" required>
              <ObjectSelect
                objects={objects}
                loading={objectsQuery.isLoading}
                value={selectedObject}
                onChange={(value) => setSelectedObject(value)}
                disabled={!isNew && Boolean(id)}
                style={{ maxWidth: 420 }}
              />
            </Form.Item>

            <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
              <Form.Item name="name" label="Название" style={{ flex: 1, minWidth: 240 }}>
                <Input placeholder="Название группы" />
              </Form.Item>
              <Form.Item
                name="level"
                label="Уровень"
                style={{ width: 180 }}
                rules={[{ required: true, message: "Укажите уровень" }]}
              >
                <Select<number>
                  options={[
                    { label: "1 уровень", value: 1 },
                    { label: "2 уровень", value: 2 },
                  ]}
                  disabled={!isNew && Boolean(id)}
                />
              </Form.Item>
            </Space>

            <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
              <Form.Item name="measurement_type" label="Тип учёта" style={{ width: 180 }}>
                <InputNumber style={{ width: "100%" }} placeholder="Тип" />
              </Form.Item>
              <Form.Item name="interval_minutes" label="Интервал" style={{ width: 180 }}>
                <InputNumber style={{ width: "100%" }} placeholder="Мин." />
              </Form.Item>
              <Form.Item name="balance_element" label="Баланс" style={{ width: 180 }}>
                <InputNumber style={{ width: "100%" }} placeholder="Код" />
              </Form.Item>
              <Form.Item name="zone" label="Зона" style={{ width: 180 }}>
                <InputNumber style={{ width: "100%" }} placeholder="Зона" />
              </Form.Item>
            </Space>

            <Form.Item name="note" label="Примечание" style={{ maxWidth: 640 }}>
              <Input.TextArea rows={3} placeholder="Комментарий" />
            </Form.Item>

            {level === 1 && (
              <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                <Typography.Title level={5}>Счётчики в группе</Typography.Title>
                <Space orientation="horizontal" size="small" wrap align="baseline">
                  <Input.Search
                    allowClear
                    placeholder="Поиск по серийному номеру"
                    onSearch={(value) => setChannelSearch(value.trim())}
                    style={{ width: 260 }}
                  />
                  <Select<number>
                    showSearch
                    placeholder="Добавить счётчик"
                    style={{ minWidth: 320 }}
                    onSelect={(value) => addChannel(value)}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ??
                      false
                    }
                    options={availableChannels.map((row) => ({
                      label: `${row.meter_number} — ${row.channel_label ?? row.serial_number ?? "без названия"}`,
                      value: row.meter_number,
                    }))}
                  />
                </Space>
                <EnterpriseTable
                  rowKey="channel_id"
                  dataSource={channels}
                  columns={channelColumns}
                  pagination={channelPagination}
                  size="small"
                  toolbar={{ reload: true, onReload: () => channelsQuery.refetch() }}
                />
              </Space>
            )}

            {level === 2 && (
              <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                <Typography.Title level={5}>Группы 1 уровня</Typography.Title>
                <Select
                  showSearch
                  placeholder="Добавить группу"
                  style={{ minWidth: 320 }}
                  onSelect={(value: string) => addMember(value)}
                  options={availableGroups.map((group) => ({ label: group.name, value: group.id }))}
                />
                <EnterpriseTable
                  rowKey="child_group_id"
                  dataSource={members}
                  columns={memberColumns}
                  pagination={memberPagination}
                  size="small"
                  toolbar={{ reload: true, onReload: () => memberOptionsQuery.refetch() }}
                />
              </Space>
            )}
          </Form>
        </Card>
      </Space>
    </PageLayout>
  );
};

export default AdminGroupDetailPage;
